/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useState } from "react"

import type { ToastOptionsResolved, ToastPushOptions } from "./types"

const DEFAULT_DISMISS_LABEL = "Dismiss"
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)"
const TOAST_ID_PREFIX = "toast-"
const TOAST_ID_RADIX = 36

let toastSeed = Date.now()

export const generateToastId = () => {
  toastSeed += 1
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID()
  }
  return `${TOAST_ID_PREFIX}${toastSeed.toString(TOAST_ID_RADIX)}`
}

export const usePrefersReducedMotion = () => {
  const [prefers, setPrefers] = useState(false)

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    ) {
      return undefined
    }

    const mediaQuery = window.matchMedia(REDUCED_MOTION_QUERY)
    const handleChange = (event: MediaQueryListEvent) => {
      setPrefers(event.matches)
    }

    setPrefers(mediaQuery.matches)

    mediaQuery.addEventListener("change", handleChange)
    return () => mediaQuery.removeEventListener("change", handleChange)
  }, [])

  return prefers
}

export const resolveOptions = (
  previous: ToastOptionsResolved | undefined,
  incoming: ToastPushOptions
  // eslint-disable-next-line complexity
): ToastOptionsResolved => {
  const {
    autoDismiss,
    dismissLabel,
    autoDismissDelay,
    tone,
    title,
    message,
    actions,
    onDismiss,
    ariaLive,
    onDelivered,
    solidBackground,
    toneColors
  } = incoming

  return {
    autoDismiss: autoDismiss ?? previous?.autoDismiss ?? true,
    autoDismissDelay: autoDismissDelay ?? previous?.autoDismissDelay,
    ariaLive: ariaLive ?? previous?.ariaLive,
    actions: actions ?? previous?.actions,
    dismissLabel:
      dismissLabel ?? previous?.dismissLabel ?? DEFAULT_DISMISS_LABEL,
    message: message ?? previous?.message ?? null,
    onDismiss: onDismiss ?? previous?.onDismiss,
    onDelivered: onDelivered ?? previous?.onDelivered,
    solidBackground: solidBackground ?? previous?.solidBackground,
    title: title ?? previous?.title,
    tone: tone ?? previous?.tone ?? ("info" as const),
    toneColors: toneColors
      ? { ...(previous?.toneColors ?? {}), ...toneColors }
      : previous?.toneColors
  }
}

export const enqueueMicrotask = (callback: () => void) => {
  if (typeof queueMicrotask === "function") {
    queueMicrotask(callback)
    return
  }

  Promise.resolve()
    .then(() => {
      callback()
    })
    .catch(() => {
      /* swallow microtask errors */
    })
}
